import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, CheckCircle, Snowflake, WashingMachine, Utensils, Flame } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { BrandsSlider } from '../components/BrandsSlider';
import { Testimonials } from '../components/Testimonials';

const northYorkSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Infinity Appliances Repair - North York",
  "telephone": "+1-647-919-6893",
  "priceRange": "$$",
  "address": { "@type": "PostalAddress", "addressLocality": "North York", "addressRegion": "ON", "addressCountry": "CA" },
  "areaServed": { "@type": "City", "name": "North York" },
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "150" }
};

const services = [
  { icon: Snowflake, title: 'Refrigerator Repair', description: 'Expert fridge diagnostics and repair' },
  { icon: WashingMachine, title: 'Washer & Dryer', description: 'Same-day laundry appliance service' },
  { icon: Utensils, title: 'Dishwasher Repair', description: 'Fix leaks and drainage issues' },
  { icon: Flame, title: 'Stove & Oven', description: 'Get cooking again fast' },
];

export const NorthYorkPage = () => {
  useEffect(() => {
    document.title = 'North York Appliance Repair | Certified Techs | Infinity';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = 'Reliable appliance repair in North York. Certified technicians serving Willowdale to Jane & Finch. Same-day fridge, washer & oven repairs. Call 647-919-6893.';
    let schemaScript = document.querySelector('#north-york-schema');
    if (!schemaScript) { schemaScript = document.createElement('script'); schemaScript.id = 'north-york-schema'; schemaScript.type = 'application/ld+json'; document.head.appendChild(schemaScript); }
    schemaScript.textContent = JSON.stringify(northYorkSchema);
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="north-york-page">
      <section className="hero-overlay relative min-h-[70vh] flex items-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1486325212027-8081e485255e?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <nav className="mb-6 text-white/70 text-sm"><Link to="/" className="hover:text-white">Home</Link><span className="mx-2">/</span><span className="text-white">North York</span></nav>
            <div className="trust-badge mb-8"><MapPin size={18} className="text-[#FF6B00]" /><span className="text-white font-semibold">Serving North York & Surrounding Areas</span></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>Reliable Appliance Repair Services in North York</h1>
            <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-2xl">Serving the North York corridor from Willowdale to Jane and Finch. We specialize in fixing major brands for busy North York families so you never miss a beat.</p>
            <a href="tel:647-919-6893" className="pulse-cta inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105 shadow-xl" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="north-york-hero-cta"><Phone size={22} /><span>Call 647-919-6893</span></a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>North York's Appliance Repair Specialists</h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">From the bustling streets of Willowdale to the diverse communities near York University, our North York technicians understand the unique needs of families in this vibrant area.</p>
              <p className="text-[#475569] leading-relaxed mb-8">We serve all North York neighborhoods including Don Mills, Bayview Village, Downsview, and the Yonge Street corridor. Our technicians arrive within 2 hours and come prepared to fix your appliance on the first visit.</p>
              <div className="space-y-4 mb-8">
                {['2-Hour Arrival in North York', 'Certified Technicians', 'Same-Day Repairs Available', 'All Major Brands Serviced', 'Transparent Pricing'].map((feature) => (
                  <div key={feature} className="flex items-center gap-3"><CheckCircle size={20} style={{ color: '#FF6B00' }} /><span className="font-medium" style={{ color: '#1A2B4C' }}>{feature}</span></div>
                ))}
              </div>
              <a href="tel:647-919-6893" className="inline-flex items-center gap-3 px-6 py-3 rounded-full font-bold text-white transition-transform hover:scale-105" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="north-york-content-cta"><Phone size={18} /><span>Call for North York Service</span></a>
            </div>
            <div>
              <div className="map-container rounded-2xl overflow-hidden shadow-xl">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92316.58!2d-79.4489!3d43.7615!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b2d127f49c19d%3A0x19a2e4a8c0b3d9e8!2sNorth%20York%2C%20Toronto%2C%20ON!5e0!3m2!1sen!2sca!4v1710000000011" width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="North York Service Area Map" data-testid="north-york-map"></iframe>
              </div>
              <p className="text-center text-[#475569] mt-4 text-sm">Serving all North York neighborhoods including Willowdale, Don Mills, Bayview Village & Downsview</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8F9FC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-10" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>Our North York Repair Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => { const IconComponent = service.icon; return (
              <Card key={service.title} className="glass-card border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(255, 107, 0, 0.1)' }}><IconComponent size={28} style={{ color: '#FF6B00' }} /></div>
                  <h3 className="font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>{service.title}</h3>
                  <p className="text-sm text-[#475569]">{service.description}</p>
                </CardContent>
              </Card>
            ); })}
          </div>
        </div>
      </section>

      <BrandsSlider />
      <Testimonials />

      <section className="py-16" style={{ background: '#1A2B4C' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Need Appliance Repair in North York?</h2>
          <p className="text-white/70 mb-8 text-lg">Our North York technicians are ready to help. Same-day service available.</p>
          <a href="tel:647-919-6893" className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="north-york-final-cta"><Phone size={22} /><span>Call 647-919-6893 Now</span></a>
        </div>
      </section>
    </main>
  );
};

export default NorthYorkPage;
